import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { UserRole } from '@magnetar/admin-api-interfaces'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ArrowUpDown, Settings } from 'lucide-react'
import { PermissionSettingDialog } from './PermissionSettingDialog'
import { UserRoleSettingDialog } from './UserRoleSettingDialog'
import { DeleteAlert } from './DeleteAlert'
import IfAnyPermission from '@/components/IfAnyPermission'
import { AllowedAction } from '@/data/allowedActions'
import { roleWritePermission } from './roleActionPermission'

type UserRoleTableMeta = {
  reloadData?: () => void
}

export const columns: ColumnDef<UserRole>[] = [
  {
    accessorKey: 'user_role_id',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          ID
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: ({ row }) => (
      <Badge variant="outline" className="font-medium">
        {row.getValue('user_role_id')}
      </Badge>
    ),
  },
  {
    accessorKey: 'role_name',
    header: 'Role Name',
  },
  {
    accessorKey: 'role_description',
    header: 'Description',
    cell: ({ row }) => (
      <div className="text-muted-foreground">{row.getValue('role_description')}</div>
    ),
  },
  {
    id: 'actions',
    cell: function ActionsCell({ row, table }) {
      const role = row.original
      const [showPermissionDialog, setShowPermissionDialog] = useState(false)
      const [showEditDialog, setShowEditDialog] = useState(false)
      const [showDeleteAlert, setShowDeleteAlert] = useState(false)

      // Write-action gates mirror the backend per-scope role endpoints (matrix X): only
      // the owning provider may edit / set-permissions / delete a role; sub-units don't
      // manage their own roles, and sys_admin is read-only on org scopes. requireExplicit-
      // ForAdmin stops sys_admin from auto-seeing org writes it would get 403 on.
      const editPerm = roleWritePermission(role.scope_type, AllowedAction.U)
      const deletePerm = roleWritePermission(role.scope_type, AllowedAction.D)

      return (
        <>
          {/* Hide the whole trigger when the user can do no write action on this role. */}
          <IfAnyPermission permissions={[...editPerm, ...deletePerm]} requireExplicitForAdmin>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <Settings className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>操作</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <IfAnyPermission permissions={editPerm} requireExplicitForAdmin>
                  <DropdownMenuItem onSelect={() => setShowEditDialog(true)}>
                    編輯基本資料
                  </DropdownMenuItem>
                </IfAnyPermission>
                <IfAnyPermission permissions={editPerm} requireExplicitForAdmin>
                  <DropdownMenuItem onSelect={() => setShowPermissionDialog(true)}>
                    設定權限
                  </DropdownMenuItem>
                </IfAnyPermission>
                <IfAnyPermission permissions={deletePerm} requireExplicitForAdmin>
                  <DropdownMenuItem
                    className="text-destructive"
                    onSelect={() => setShowDeleteAlert(true)}
                  >
                    刪除
                  </DropdownMenuItem>
                </IfAnyPermission>
              </DropdownMenuContent>
            </DropdownMenu>
          </IfAnyPermission>

          {showPermissionDialog && (
            <PermissionSettingDialog
              role={role}
              open={showPermissionDialog}
              onOpenChange={setShowPermissionDialog}
            />
          )}

          {showEditDialog && (
            <UserRoleSettingDialog
              role={role}
              open={showEditDialog}
              onOpenChange={setShowEditDialog}
              onSuccess={() => (table.options.meta as UserRoleTableMeta)?.reloadData?.()}
            />
          )}

          {showDeleteAlert && (
            <DeleteAlert
              role={role}
              open={showDeleteAlert}
              onOpenChange={setShowDeleteAlert}
              onSuccess={() => (table.options.meta as UserRoleTableMeta)?.reloadData?.()}
            />
          )}
        </>
      )
    },
  },
]
